import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { notificationRoute, type NotificationRow } from '@st/contract';
import { notificationTitle } from '../lib/notificationTitle';
import {
  useMarkAllNotificationsRead,
  useMarkNotification,
  useNotifications,
  useUnreadNotificationCount,
  type NotificationFilter,
} from '../api/queries';
import { QueryBoundary } from './QueryBoundary';
import { useTranslation } from '../i18n/I18nProvider';
import { toErrorMessage } from '../i18n/errorMessage';
import { formatDay, formatTime } from '../lib/format';
import { cx } from '../lib/cx';
import { useAuth } from '../auth/AuthProvider';
import { Button, SkeletonList, useToast } from '../ui';
import { IconBell } from './icons';

/**
 * Zvonce u zaglavlju.
 *
 * Badge čita SAMO `unreadCount()` — nikad cijelu listu (prethodni projekt je za
 * jedan broj dohvaćao 80 zapisa). Lista se dohvaća tek kad se popis otvori, a
 * otvaranje popisa NIKAD ne označava obavijesti pročitanima: to je bio bug koji
 * je pojeo cijeli povijesni pregled.
 */
export function NotificationBell({ className }: { className?: string }) {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const panelRef = useRef<HTMLDivElement | null>(null);
  const bellRef = useRef<HTMLButtonElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(
    null,
  );

  const unread = useUnreadNotificationCount();
  const list = useNotifications(filter, open);
  const mark = useMarkNotification();
  const markAll = useMarkAllNotificationsRead();

  const count = unread.data?.count ?? 0;

  /*
    Popis je bio `absolute right-0` uz zvonce. Zvonce na desktopu stoji u uskoj
    bocnoj traci, pa je popis od 22rem izlazio lijevo izvan zaslona i bio je
    odrezan. Zato se sada racuna polozaj iz pravokutnika zvonca i drzi unutar
    vidljivog podrucja s obje strane.
  */
  const reposition = useCallback(() => {
    const bell = bellRef.current;
    if (!bell) return;
    const r = bell.getBoundingClientRect();
    const margin = 12;
    const width = Math.min(352, window.innerWidth - margin * 2);
    const maxLeft = window.innerWidth - width - margin;
    const left = Math.min(Math.max(margin, r.right - width), Math.max(margin, maxLeft));
    const top = r.bottom + 8;
    // Ispod 1024 px donja navigacija prekriva zaslon, pa popis mora stati iznad nje.
    const bottomGap = window.innerWidth < 1024 ? 88 : 16;
    const maxHeight = Math.max(200, window.innerHeight - top - bottomGap);
    setPos({ top, left, width, maxHeight });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    // `true` hvata i pomicanje unutar unutarnjih spremnika, ne samo prozora.
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // Escape zatvara popis i vraća fokus na zvonce — inače fokus ostane na
      // elementu koji je upravo nestao i korisnik tipkovnice padne na `<body>`.
      if (e.key !== 'Escape') return;
      setOpen(false);
      bellRef.current?.focus();
    };
    const onPointer = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  function onOpenNotification(item: NotificationRow): void {
    setOpen(false);
    // Klik na obavijest vodi na deepLink; oznaka pročitano je posljedica klika,
    // ne posljedica otvaranja popisa.
    if (item.readAt === null) mark.mutate({ id: item.id, read: true });
    const ruta = notificationRoute(item.deepLink, user?.role);
    if (ruta) void navigate(ruta);
  }

  return (
    <div ref={panelRef} className={cx('relative', className)}>
      <button
        ref={bellRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={count > 0 ? t('notif.unreadCount', { n: count }) : t('notif.open')}
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-full p-2 text-ink-muted transition-colors hover:bg-bg-alt hover:text-ink"
      >
        <IconBell />
        {count > 0 && (
          <span
            data-testid="notification-badge"
            className={cx(
              'absolute -right-0.5 -top-0.5 min-w-[18px] rounded-pill bg-accent-dark px-1',
              'text-center text-[10px] leading-[18px] text-surface tabular-nums',
            )}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && pos && (
        <div
          role="dialog"
          aria-label={t('notif.title')}
          style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
          className={cx(
            'fixed z-50 flex flex-col',
            'rounded-xl border border-line-subtle bg-surface shadow-panel',
          )}
        >
          <div className="flex items-center justify-between gap-2 border-b border-line-subtle px-4 py-3">
            <h2 className="font-display text-[16px] font-semibold text-ink">{t('notif.title')}</h2>
            <Button
              variant="ghost"
              size="sm"
              disabled={count === 0}
              loading={markAll.isPending}
              onClick={() => {
                markAll.mutate(undefined, {
                  onError: (err) => toast(toErrorMessage(t, err).title, 'danger'),
                });
              }}
            >
              {t('notif.markAllRead')}
            </Button>
          </div>

          {/*
            Prije je ovo bio `role="tablist"` bez `tabpanel`-a i bez navigacije
            strelicama — čitač zaslona je najavljivao karticu koje nema. To su
            dva preklopnika filtra, pa nose `aria-pressed`.
          */}
          <div role="group" aria-label={t('notif.title')} className="flex gap-1 px-3 py-2">
            {(['all', 'unread'] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={filter === value}
                onClick={() => setFilter(value)}
                className={cx(
                  'rounded-pill px-3 py-1 text-[12.5px] transition-colors',
                  filter === value
                    ? 'bg-accent-light text-accent-deep'
                    : 'text-ink-muted hover:bg-bg-alt',
                )}
              >
                {value === 'all' ? t('notif.filterAll') : t('notif.filterUnread')}
              </button>
            ))}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
            <QueryBoundary
              query={list}
              skeleton={<SkeletonList rows={3} />}
              isEmpty={(d) => d.items.length === 0}
              empty={{ message: filter === 'unread' ? t('notif.emptyUnread') : t('notif.empty') }}
            >
              {(data) => (
                <ul className="flex flex-col gap-1">
                  {data.items.map((item) => (
                    <li
                      key={item.id}
                      className={cx(
                        'rounded-md p-2 transition-colors',
                        item.readAt === null ? 'bg-accent-light/40' : 'bg-transparent',
                      )}
                    >
                      <div className="flex items-start gap-2">
                        <button
                          type="button"
                          onClick={() => onOpenNotification(item)}
                          className="min-w-0 flex-1 text-left"
                        >
                          <span className="block text-[14px] text-ink">{notificationTitle(t, item)}</span>
                          <span className="mt-0.5 block text-[11px] text-ink-muted">
                            {formatDay(item.createdAt, locale)} · {formatTime(item.createdAt, locale)}
                          </span>
                        </button>
                        {/* Meta je 24 px; točka unutar nje ostaje 10 px kakva je bila. */}
                        <button
                          type="button"
                          aria-label={item.readAt === null ? t('notif.markRead') : t('notif.markUnread')}
                          title={item.readAt === null ? t('notif.markRead') : t('notif.markUnread')}
                          onClick={() => mark.mutate({ id: item.id, read: item.readAt === null })}
                          className="-mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                        >
                          <span
                            aria-hidden="true"
                            className={cx(
                              'block h-2.5 w-2.5 rounded-full border transition-colors',
                              item.readAt === null
                                ? 'border-accent-dark bg-accent-dark'
                                : 'border-line-strong bg-transparent',
                            )}
                          />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </QueryBoundary>
          </div>
        </div>
      )}
    </div>
  );
}
